<template>
  <div>
    <p class="text-muted mb-2">{{ $t('stripe_connect.help') }}</p>

    <div v-if="!status">
      <b-alert variant="secondary" show class="p-1">{{ $t('stripe_connect.loading') }}</b-alert>
    </div>

    <template v-else>
      <div class="vx-row mb-1">
        <div class="vx-col w-full">
          <vs-chip
            transparent
            :color="statusColor"
            class="text-base mr-2">
            {{ statusLabel }}
          </vs-chip>
          <span v-if="status.external_account_id" class="text-muted">{{ status.external_account_id }}</span>
        </div>
      </div>

      <div class="vx-row mb-1" v-if="pendingRequirements.length">
        <div class="vx-col w-full">
          <b-alert variant="warning" show class="p-1">
            <strong>{{ $t('stripe_connect.pending') }}</strong>
            <ul class="mb-0 mt-1">
              <li v-for="item in pendingRequirements" :key="item">{{ item }}</li>
            </ul>
          </b-alert>
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full flex gap-2">
          <b-button
            variant="primary"
            :disabled="!legalEntityId"
            @click="onboard">
            {{ status.connected ? $t('stripe_connect.continue') : $t('stripe_connect.start') }}
          </b-button>
          <b-button variant="outline-secondary" @click="loadStatus">
            {{ $t('stripe_connect.refresh') }}
          </b-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { BAlert, BButton } from 'bootstrap-vue'
import StripeConnectService from '@/services/api/StripeConnectService'

/**
 * Conta do clube no Stripe Connect.
 *
 * A cobrança pelo Stripe usa destination charge: a comissão da Hubee fica como
 * `application_fee_amount` e o restante vai para a conta abaixo. Enquanto ela não
 * estiver com cobranças liberadas, a venda acontece mas sem split — o valor
 * inteiro fica na conta da plataforma.
 */
export default {
  name: 'StripeConnect',
  components: { BAlert, BButton },
  props: {
    legalEntityId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      service: null,
      status: null
    }
  },
  computed: {
    statusColor() {
      if (!this.status?.connected) return 'danger'
      return this.status.charges_enabled ? 'success' : 'warning'
    },
    statusLabel() {
      if (!this.status?.connected) return this.$t('stripe_connect.not_connected')
      return this.status.charges_enabled
        ? this.$t('stripe_connect.enabled')
        : this.$t('stripe_connect.incomplete')
    },
    pendingRequirements() {
      const due = this.status?.requirements_due || {}
      return [...(due.past_due || []), ...(due.currently_due || [])]
    }
  },
  methods: {
    loadStatus() {
      if (!this.legalEntityId) return
      this.$vs.loading()
      return this.service
        .getStatus(this.legalEntityId)
        .then(data => {
          this.status = data
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => this.$vs.loading.close())
    },
    onboard() {
      // O cadastro é hospedado pelo Stripe: sem aviso, quem clica é levado para
      // fora do Hubee sem entender por quê. Confirma antes de sair.
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Você vai sair do Hubee',
        acceptText: 'Continuar para o Stripe',
        cancelText: 'Cancelar',
        text: 'O cadastro de recebimento é feito no site do Stripe. Você será redirecionado para lá e' +
          ' pode voltar ao Hubee quando terminar.',
        accept: this.goToStripe,
      })
    },
    goToStripe() {
      this.$vs.loading()
      return this.service
        .startOnboarding(this.legalEntityId)
        .then(data => {
          // O Account Link é de uso único e expira; abrir na hora evita link velho.
          window.location.href = data.url
        })
        .catch(error => {
          this.showErrors(null, error, this)
          this.$vs.loading.close()
        })
    }
  },
  beforeMount() {
    this.service = StripeConnectService.build(this.$vs)
  },
  mounted() {
    this.loadStatus()
  }
}
</script>
